import {
  SOCIAL_BRAND_HASHTAGS_MAX,
  SOCIAL_BRAND_SEARCHES_MAX,
  SOCIAL_COMMUNITY_MODES,
  SOCIAL_PLATFORM_SEARCHES_BRANDS,
  SOCIAL_PLATFORM_SOURCE_KINDS,
  SOCIAL_SOURCE_UNCHECKED,
  SOCIAL_DEFAULT_CADENCE,
  SOCIAL_DEFAULT_DEPTH_DAYS,
  SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT,
  SOCIAL_DEFAULT_RUN_POST_LIMIT,
  SOCIAL_MAX_BRANDS,
  SOCIAL_MAX_KEYWORDS,
  SOCIAL_MAX_LANGUAGES,
  SOCIAL_MAX_TOPICS,
  socialInputKey,
  SocialBrandAccount,
  SocialBrandInput,
  SocialChannelCommunityInput,
  SocialChannelHashtagInput,
  SocialChannelInput,
  SocialChannelSearchInput,
  SocialCommunityMode,
  SocialDepthDays,
  SocialEstimateRequest,
  SocialKeywordNote,
  SocialPlatform,
  SocialRunPostLimit,
  SocialFirstCollectionPostLimit,
  SocialSourceCheck,
  SocialSubjectKind,
  SocialSourceCheckFailure,
  SocialSourceCheckStatus,
  SocialTopicInput,
  SocialTrackerBrief,
  SocialTrackerCadence,
  SocialTrackerLabel,
  SocialUniverseInput,
  SocialUniverseItem,
  SocialUniverseSuggestion,
  SocialUniverseUpdate,
  socialTypedChannelInputs,
} from '@digital-twins/shared-types'
import { addSocialTerms, withLabelValues } from './socialTrackerForm'
import { SocialMarketValue, socialMarketDefaultLanguage } from './socialMarket'

import { SocialTermNoteTone } from '@/types/components/social-tracker-dialog'

/** Channel order in the dialog, as in the New topic mockup. */
export const SOCIAL_UNIVERSE_PLATFORM_ORDER: Array<SocialPlatform> = [
  'tiktok',
  'instagram',
  'youtube',
  'reddit',
]

/** Most accounts a channel follows. */
export const SOCIAL_UNIVERSE_MAX_HANDLES = 20

/** Reddit narrows to one community (the provider's limit). */
export const SOCIAL_UNIVERSE_MAX_NARROW_COMMUNITIES = 1

export const SOCIAL_UNIVERSE_DEFAULT_LANGUAGES: Array<string> = ['en']

/**
 * The Topic's market after a pick, its languages following it (D: language
 * follows the market): while the languages are still the previous market's
 * default (or the form's own default before any market), they become the new
 * market's default (`socialMarketDefaultLanguage`); a language the user
 * picked is kept, and a market with no default leaves them as they are.
 */
export function withUniverseMarket(
  form: Pick<UniverseFormState, 'market' | 'marketCode' | 'languages'>,
  picked: SocialMarketValue,
): Pick<UniverseFormState, 'market' | 'marketCode' | 'languages'> {
  const previousDefault = form.marketCode
    ? socialMarketDefaultLanguage(form.marketCode)
    : null
  const followed = previousDefault ? [previousDefault] : SOCIAL_UNIVERSE_DEFAULT_LANGUAGES
  const following =
    form.languages.length === 0 ||
    (form.languages.length === followed.length &&
      form.languages.every((code, index) => code === followed[index]))
  const nextDefault = socialMarketDefaultLanguage(picked.marketCode)
  return {
    market: picked.market,
    marketCode: picked.marketCode,
    languages: following && nextDefault ? [nextDefault] : form.languages,
  }
}

/**
 * Where a source's check stands while it has no answer: `checking` while its
 * call runs; then why it stayed unchecked, the result's typed `failure`, or
 * `request_failed` when the check call itself failed. Shown beside the
 * source; never silent.
 */
export type SocialSourceCheckState = 'checking' | 'request_failed' | SocialSourceCheckFailure

export type SourceCheckLabelKey =
  | 'socialListening.topicDialog.sourceChecking'
  | `socialListening.topicDialog.accountCheck.${Exclude<SocialSourceCheckStatus, 'unchecked'>}`
  | `socialListening.topicDialog.accountCheckFailed.${Exclude<SocialSourceCheckState, 'checking'>}`

/**
 * The dot a check label shows beside its source: verified a success, not
 * found destructive, a running check pending, anything else (inactive, age
 * restricted, couldn't check) muted.
 */
export function sourceCheckTone(key: SourceCheckLabelKey): SocialTermNoteTone {
  if (key === 'socialListening.topicDialog.sourceChecking') {
    return 'pending'
  }
  if (key === 'socialListening.topicDialog.accountCheck.verified') {
    return 'success'
  }
  if (key === 'socialListening.topicDialog.accountCheck.not_found') {
    return 'destructive'
  }
  return 'muted'
}

/** The i18n key of a source's check label: running, why it gave no answer, or what it found; null before any check. */
export function sourceCheckLabelKey(
  check: SocialSourceCheck | undefined,
  state: SocialSourceCheckState | null,
): SourceCheckLabelKey | null {
  if (state === 'checking') {
    return 'socialListening.topicDialog.sourceChecking'
  }
  if (check && check.status !== 'unchecked') {
    return `socialListening.topicDialog.accountCheck.${check.status}`
  }
  return state ? `socialListening.topicDialog.accountCheckFailed.${state}` : null
}

export interface UniverseFormState {
  name: string
  /** What the client wants to learn: Suggest writes the brief from it. Never saved. */
  goal: string
  keywords: Array<string>
  keywordNotes: Array<SocialKeywordNote>
  brief: SocialTrackerBrief
  labels: Array<SocialTrackerLabel>
  /** Themes (internal `topics`). */
  topics: Array<SocialTopicInput>
  brands: Array<SocialBrandInput>
  /** What the Topic is about (D24): Suggest fills it, the user may change it on step 1. */
  subjectKind: SocialSubjectKind
  platforms: Array<SocialPlatform>
  /** What each channel searches for; Suggest fills them, the user edits them. A kept search keeps its id. */
  searches: Record<SocialPlatform, Array<SocialChannelSearchInput>>
  /** Hashtags, on channels that follow them (SOCIAL_PLATFORM_SOURCE_KINDS), without the `#`. */
  hashtags: Record<SocialPlatform, Array<SocialChannelHashtagInput>>
  /** Communities, on channels that take them: `narrow` typed by the client, `add` from Suggest. */
  communities: Record<SocialPlatform, Array<SocialChannelCommunityInput>>
  /** Accounts a channel follows, on channels that offer accounts. */
  handles: Record<SocialPlatform, Array<string>>
  languages: Array<string>
  market: string
  /** The picked country's code (`socialMarket.ts`); null when the market has none. */
  marketCode: string | null
  depthDays: SocialDepthDays
  cadence: SocialTrackerCadence
  /** Relevant posts a week per channel (SOCIAL_RUN_POST_LIMITS). */
  runPostLimit: SocialRunPostLimit
  /** Most posts per channel a first collection keeps (SOCIAL_FIRST_COLLECTION_POST_LIMITS). */
  firstCollectionPostLimit: SocialFirstCollectionPostLimit
}

export type UniverseRequiredField =
  | 'name'
  | 'keywords'
  | 'channels'
  | 'searches'
  | 'languages'
  | 'market'

export interface UniverseDrafts {
  keywords: string
  brands: string
  searches: Record<SocialPlatform, string>
  hashtags: Record<SocialPlatform, string>
  /** Per community mode: the `narrow` field and the `add` field. */
  communities: Record<SocialCommunityMode, Record<SocialPlatform, string>>
  handles: Record<SocialPlatform, string>
}

function emptyPlatformLists<T>(): Record<SocialPlatform, Array<T>> {
  return { tiktok: [], instagram: [], youtube: [], reddit: [] }
}

function emptyPlatformDrafts(): Record<SocialPlatform, string> {
  return { tiktok: '', instagram: '', youtube: '', reddit: '' }
}

export function emptyUniverseDrafts(): UniverseDrafts {
  return {
    keywords: '',
    brands: '',
    searches: emptyPlatformDrafts(),
    hashtags: emptyPlatformDrafts(),
    communities: { narrow: emptyPlatformDrafts(), add: emptyPlatformDrafts() },
    handles: emptyPlatformDrafts(),
  }
}

/**
 * Chip values onto typed entries: a value that is already an entry keeps that
 * entry whole (its id and check), a new value becomes `create(value)`; order
 * follows `values`.
 */
export function withChipValues<T>(
  entries: Array<T>,
  values: Array<string>,
  valueOf: (entry: T) => string,
  create: (value: string) => T,
): Array<T> {
  return values.map((value) => entries.find((entry) => valueOf(entry) === value) ?? create(value))
}

/** A hashtag as the chip shows and the server takes it: without its leading `#`. */
export function socialHashtagValue(value: string): string {
  return value.trim().replace(/^#+/, '')
}

/** A platform's communities of one mode, as chip values. */
export function communityNames(
  form: Pick<UniverseFormState, 'communities'>,
  platform: SocialPlatform,
  mode: SocialCommunityMode,
): Array<string> {
  return form.communities[platform]
    .filter((community) => community.mode === mode)
    .map((community) => community.name)
}

/**
 * Replaces one mode's communities of a platform with `names`, keeping the
 * other mode's; a name whose `socialInputKey` an earlier one has is the
 * same community and is left out (the first spelling stays).
 */
export function withCommunityNames(
  communities: Array<SocialChannelCommunityInput>,
  mode: SocialCommunityMode,
  names: Array<string>,
): Array<SocialChannelCommunityInput> {
  const own = communities.filter((community) => community.mode === mode)
  const keys = new Set<string>()
  const unique = names.filter((name) => {
    const key = socialInputKey(name)
    if (keys.has(key)) {
      return false
    }
    keys.add(key)
    return true
  })
  const kept = withChipValues(own, unique, (entry) => entry.name, (name) => ({ name, mode }))
  return [...communities.filter((community) => community.mode !== mode), ...kept]
}

/** Searches and hashtags left before the channel holds SOCIAL_MAX_KEYWORDS. */
export function channelInputRoom(form: UniverseFormState, platform: SocialPlatform): number {
  return Math.max(
    0,
    SOCIAL_MAX_KEYWORDS - form.searches[platform].length - form.hashtags[platform].length,
  )
}

function sortPlatforms(platforms: Array<SocialPlatform>): Array<SocialPlatform> {
  return SOCIAL_UNIVERSE_PLATFORM_ORDER.filter((platform) =>
    platforms.includes(platform),
  )
}

/** A new Topic starts on every channel with the product defaults; an edit starts from the Topic. */
export function initialUniverseForm(
  universe: SocialUniverseItem | null,
): UniverseFormState {
  if (!universe) {
    return {
      name: '',
      goal: '',
      keywords: [],
      keywordNotes: [],
      brief: { include: '', exclude: '' },
      labels: [],
      topics: [],
      brands: [],
      subjectKind: 'category',
      platforms: [...SOCIAL_UNIVERSE_PLATFORM_ORDER],
      searches: emptyPlatformLists(),
      hashtags: emptyPlatformLists(),
      communities: emptyPlatformLists(),
      handles: emptyPlatformLists(),
      languages: [...SOCIAL_UNIVERSE_DEFAULT_LANGUAGES],
      market: '',
      marketCode: null,
      depthDays: SOCIAL_DEFAULT_DEPTH_DAYS,
      cadence: SOCIAL_DEFAULT_CADENCE,
      runPostLimit: SOCIAL_DEFAULT_RUN_POST_LIMIT,
      firstCollectionPostLimit: SOCIAL_DEFAULT_FIRST_COLLECTION_POST_LIMIT,
    }
  }
  const handles = emptyPlatformLists<string>()
  const searches = emptyPlatformLists<SocialChannelSearchInput>()
  const hashtags = emptyPlatformLists<SocialChannelHashtagInput>()
  const communities = emptyPlatformLists<SocialChannelCommunityInput>()
  for (const channel of universe.channels) {
    const { platform } = channel
    // `inputs` is the typed read; a server before it sends the old columns, typed by the one transform.
    const inputs =
      channel.inputs ??
      socialTypedChannelInputs(platform, channel.searches, channel.handles, null, () => '')
    const withId = <T extends { id: string }>(entry: T) => {
      const { id, ...rest } = entry
      return id ? entry : rest
    }
    searches[platform] = inputs.searches.map(withId)
    hashtags[platform] = inputs.hashtags.map(withId)
    communities[platform] = inputs.communities.map(withId)
    handles[platform] = SOCIAL_PLATFORM_SOURCE_KINDS[platform].account ? [...channel.handles] : []
  }
  return {
    name: universe.name,
    goal: '',
    keywords: [...universe.keywords],
    keywordNotes: universe.keywordNotes.map((note) => ({ ...note })),
    brief: { ...universe.brief },
    labels: universe.labels.map((label) => ({ ...label })),
    topics: universe.topics.map((topic) => ({ ...topic })),
    brands: universe.brands.map((brand) => ({ ...brand })),
    subjectKind: universe.subjectKind,
    platforms: sortPlatforms(universe.channels.map((channel) => channel.platform)),
    searches,
    hashtags,
    communities,
    handles,
    languages: [...universe.languages],
    market: universe.market,
    marketCode: universe.marketCode ?? null,
    depthDays: universe.depthDays,
    cadence: universe.cadence,
    runPostLimit: universe.runPostLimit,
    firstCollectionPostLimit: universe.firstCollectionPostLimit,
  }
}

/**
 * Brand chips edit names only; a kept name keeps its id, description,
 * searches, hashtags, collect flag, role, aliases and accounts. A new brand
 * starts with one search, its name, shown and editable on its row.
 */
export function withBrandNames(
  brands: Array<SocialBrandInput>,
  names: Array<string>,
): Array<SocialBrandInput> {
  return names.slice(0, SOCIAL_MAX_BRANDS).map((name) => {
    const existing = brands.find(
      (brand) => brand.name.trim().toLowerCase() === name.trim().toLowerCase(),
    )
    return existing
      ? { ...existing, name }
      : { name, description: '', searches: [{ query: name.trim() }], collect: true }
  })
}

/** Commits every pending chip draft into one snapshot before save or suggest. */
export function flushUniverseDrafts(
  form: UniverseFormState,
  drafts: UniverseDrafts,
): UniverseFormState {
  const handles = { ...form.handles }
  const searches = { ...form.searches }
  const hashtags = { ...form.hashtags }
  const communities = { ...form.communities }
  for (const platform of SOCIAL_UNIVERSE_PLATFORM_ORDER) {
    handles[platform] = addSocialTerms(
      form.handles[platform],
      drafts.handles[platform],
    ).slice(-SOCIAL_UNIVERSE_MAX_HANDLES)
    const queries = addSocialTerms(
      form.searches[platform].map((search) => search.query),
      drafts.searches[platform],
    ).slice(0, SOCIAL_MAX_KEYWORDS - form.hashtags[platform].length)
    searches[platform] = withChipValues(
      form.searches[platform],
      queries,
      (search) => search.query,
      (query) => ({ query }),
    )
    const tags = addSocialTerms(
      form.hashtags[platform].map((hashtag) => hashtag.tag),
      drafts.hashtags[platform],
    )
      .map(socialHashtagValue)
      .filter(Boolean)
      .slice(0, SOCIAL_MAX_KEYWORDS - searches[platform].length)
    hashtags[platform] = withChipValues(
      form.hashtags[platform],
      [...new Set(tags)],
      (hashtag) => hashtag.tag,
      (tag) => ({ tag }),
    )
    let next = form.communities[platform]
    for (const mode of SOCIAL_COMMUNITY_MODES) {
      const names = addSocialTerms(
        next.filter((community) => community.mode === mode).map((community) => community.name),
        drafts.communities[mode][platform],
      )
      next = withCommunityNames(
        next,
        mode,
        mode === 'narrow' ? names.slice(-SOCIAL_UNIVERSE_MAX_NARROW_COMMUNITIES) : names,
      )
    }
    communities[platform] = next
  }
  return {
    ...form,
    keywords: addSocialTerms(form.keywords, drafts.keywords),
    brands: withBrandNames(
      form.brands,
      addSocialTerms(
        form.brands.map((brand) => brand.name),
        drafts.brands,
      ),
    ),
    searches,
    hashtags,
    communities,
    handles,
  }
}

/** The selected market owns the one market tag written to Library documents. */
export function universeLabels(
  form: UniverseFormState,
): Array<SocialTrackerLabel> {
  return withLabelValues(
    form.labels,
    'market',
    form.market.trim() ? [form.market.trim()] : [],
  )
}

export function missingUniverseFields(
  form: UniverseFormState,
): Array<UniverseRequiredField> {
  const missing: Array<UniverseRequiredField> = []
  if (!form.name.trim()) {
    missing.push('name')
  }
  if (form.keywords.length === 0) {
    missing.push('keywords')
  }
  if (form.platforms.length === 0) {
    missing.push('channels')
  }
  if (platformsWithoutSearches(form).length > 0) {
    missing.push('searches')
  }
  if (form.languages.length === 0) {
    missing.push('languages')
  }
  if (!form.market.trim()) {
    missing.push('market')
  }
  return missing
}

/** Selected channels with no search and no hashtag yet; each one needs at least one. */
export function platformsWithoutSearches(
  form: UniverseFormState,
): Array<SocialPlatform> {
  return sortPlatforms(form.platforms).filter(
    (platform) => form.searches[platform].length + form.hashtags[platform].length === 0,
  )
}

/** Keywords past SOCIAL_MAX_KEYWORDS; 0 when the Topic is within the limit. */
export function keywordsOverLimit(form: UniverseFormState): number {
  return Math.max(0, form.keywords.length - SOCIAL_MAX_KEYWORDS)
}

/** The dialog's steps, in order. */
export type UniverseStep = 'topic' | 'brands' | 'channels' | 'tracking' | 'review'

export const SOCIAL_UNIVERSE_STEPS: Array<UniverseStep> = [
  'topic',
  'brands',
  'channels',
  'tracking',
  'review',
]

/** What keeps a step from moving on: a required field, or more keywords than the server takes. */
export type UniverseStepBlocker = UniverseRequiredField | 'keywordLimit'

const STEP_BLOCKERS: Record<UniverseStep, Array<UniverseStepBlocker>> = {
  // Step 1 holds what Suggest reads (goal, market, languages); step 2 what it fills
  // (D21b): the subject, what the Topic is about and the brands. The channel picker heads step 3.
  topic: ['name', 'market', 'languages'],
  brands: ['keywords', 'keywordLimit'],
  channels: ['channels', 'searches'],
  tracking: [],
  review: [],
}

/** Blockers of one step, in the order its fields appear. */
export function universeStepBlockers(
  form: UniverseFormState,
  step: UniverseStep,
): Array<UniverseStepBlocker> {
  const missing: Array<UniverseStepBlocker> = missingUniverseFields(form)
  if (keywordsOverLimit(form) > 0) {
    missing.push('keywordLimit')
  }
  return STEP_BLOCKERS[step].filter((blocker) => missing.includes(blocker))
}

/** The first step with a blocker; null when the Topic can be saved. */
export function firstBlockedUniverseStep(
  form: UniverseFormState,
): UniverseStep | null {
  return (
    SOCIAL_UNIVERSE_STEPS.find(
      (step) => universeStepBlockers(form, step).length > 0,
    ) ?? null
  )
}

function namedTopics(form: UniverseFormState): Array<SocialTopicInput> {
  return form.topics
    .filter((topic) => topic.name.trim() !== '')
    .slice(0, SOCIAL_MAX_TOPICS)
    .map((topic) => ({
      ...(topic.id ? { id: topic.id } : {}),
      name: topic.name.trim(),
      description: topic.description.trim(),
    }))
}

function namedBrands(form: UniverseFormState): Array<SocialBrandInput> {
  return form.brands
    .filter((brand) => brand.name.trim() !== '')
    .slice(0, SOCIAL_MAX_BRANDS)
    .map((brand) => {
      const aliases = (brand.aliases ?? []).map((alias) => alias.trim()).filter(Boolean)
      return {
        ...(brand.id ? { id: brand.id } : {}),
        name: brand.name.trim(),
        description: (brand.description ?? '').trim(),
        searches: brandSearchesOrName(brand).map((search) => ({
          ...(search.id ? { id: search.id } : {}),
          query: search.query,
        })),
        ...((brand.hashtags ?? []).length ? { hashtags: brand.hashtags } : {}),
        collect: brand.collect,
        ...(brand.role ? { role: brand.role } : {}),
        ...(aliases.length ? { aliases } : {}),
        ...(brand.accounts && Object.keys(brand.accounts).length
          ? { accounts: brand.accounts }
          : {}),
      }
    })
}

/** A stable id for the search a brand's name stands in for: the same name always gives the same id. */
export function brandNameSearchId(name: string): string {
  let hash = 0x811c9dc5
  for (const char of name.trim().toLowerCase()) {
    hash ^= char.codePointAt(0) ?? 0
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return `name-${hash.toString(16).padStart(8, '0')}`
}

/**
 * The searches a brand is sent with: its own, else its name as its first
 * search (owner decision), so a brand with no search still saves. The
 * server's "needs a search" refusal stays the contract guard.
 */
export function brandSearchesOrName(
  brand: Pick<SocialBrandInput, 'name' | 'searches'>,
): Array<SocialChannelSearchInput> {
  const searches = brandSearchQueries(brand)
  const name = brand.name.trim()
  if (searches.length > 0 || name === '') {
    return searches
  }
  return [{ id: brandNameSearchId(name), query: name }]
}

/** A brand's searches with words, trimmed, at most SOCIAL_BRAND_SEARCHES_MAX (D20). */
export function brandSearchQueries(
  brand: Pick<SocialBrandInput, 'searches'>,
): Array<SocialChannelSearchInput> {
  return (brand.searches ?? [])
    .map((search) => ({ ...search, query: search.query.trim() }))
    .filter((search) => search.query !== '')
    .slice(0, SOCIAL_BRAND_SEARCHES_MAX)
}

/** Brand search chips onto the brand's typed searches: a kept query keeps its id. */
export function withBrandSearchValues(
  brand: SocialBrandInput,
  values: Array<string>,
): SocialBrandInput {
  return {
    ...brand,
    searches: withChipValues(
      brand.searches ?? [],
      values.slice(0, SOCIAL_BRAND_SEARCHES_MAX),
      (search) => search.query,
      (query) => ({ query }),
    ),
  }
}

/** Brand hashtag chips (with or without `#`) onto the brand's typed hashtags: a kept tag keeps its id and check. */
export function withBrandHashtagValues(
  brand: SocialBrandInput,
  values: Array<string>,
): SocialBrandInput {
  const tags = [...new Set(values.map(socialHashtagValue).filter(Boolean))].slice(
    0,
    SOCIAL_BRAND_HASHTAGS_MAX,
  )
  return {
    ...brand,
    hashtags: withChipValues(brand.hashtags ?? [], tags, (hashtag) => hashtag.tag, (tag) => ({ tag })),
  }
}

/**
 * The collected brands a picked channel also searches (its `search.brands`
 * rule, SOCIAL_PLATFORM_SEARCHES_BRANDS): each named brand with a search,
 * by name, for the channel card's read-only line.
 */
export function channelBrandNames(
  form: Pick<UniverseFormState, 'brands'>,
  platform: SocialPlatform,
): Array<string> {
  if (!SOCIAL_PLATFORM_SEARCHES_BRANDS[platform]) {
    return []
  }
  return form.brands
    .filter((brand) => brand.collect && brand.name.trim() !== '' && brandSearchQueries(brand).length > 0)
    .map((brand) => brand.name.trim())
}

/**
 * The "Other names" field: a brand's aliases as one comma-separated text.
 * The text round-trips as typed (spaces and a trailing comma kept while
 * typing); each alias is trimmed and empty ones dropped when sent.
 */
export function brandAliasesText(brand: Pick<SocialBrandInput, 'aliases'>): string {
  return (brand.aliases ?? []).join(',')
}

export function aliasesFromText(text: string): Array<string> {
  return text === '' ? [] : text.split(',')
}

/** A brand account the client typed: confirmed, its check still to run. */
export function typedBrandAccount(handle: string): SocialBrandAccount {
  return {
    handle: handle.trim().replace(/^@+/, ''),
    providerId: null,
    check: { ...SOCIAL_SOURCE_UNCHECKED },
    confirmed: true,
  }
}

function currentKeywordNotes(
  form: UniverseFormState,
): Array<SocialKeywordNote> {
  return form.keywordNotes.filter(
    (note) =>
      form.keywords.includes(note.keyword) && note.description.trim() !== '',
  )
}

/**
 * The typed wire of each picked channel: its searches, and its hashtags,
 * communities and accounts where the channel takes them
 * (SOCIAL_PLATFORM_SOURCE_KINDS). An edit sends every list, empty included.
 */
function channelInputs(form: UniverseFormState, isEdit: boolean): Array<SocialChannelInput> {
  return sortPlatforms(form.platforms).map((platform) => {
    const kinds = SOCIAL_PLATFORM_SOURCE_KINDS[platform]
    const sends = (list: Array<unknown>) => isEdit || list.length > 0
    return {
      platform,
      searches: form.searches[platform],
      ...(kinds.hashtag && sends(form.hashtags[platform])
        ? { hashtags: form.hashtags[platform] }
        : {}),
      ...(kinds.community && sends(form.communities[platform])
        ? { communities: form.communities[platform] }
        : {}),
      ...(kinds.account && sends(form.handles[platform]) ? { handles: form.handles[platform] } : {}),
    }
  })
}

export function toUniverseInput(form: UniverseFormState): SocialUniverseInput {
  return {
    name: form.name.trim(),
    keywords: form.keywords,
    keywordNotes: currentKeywordNotes(form),
    brief: form.brief,
    labels: universeLabels(form),
    topics: namedTopics(form),
    brands: namedBrands(form),
    subjectKind: form.subjectKind,
    channels: channelInputs(form, false),
    languages: form.languages.slice(0, SOCIAL_MAX_LANGUAGES),
    market: form.market.trim(),
    marketCode: form.marketCode,
    depthDays: form.depthDays,
    cadence: form.cadence,
    runPostLimit: form.runPostLimit,
    firstCollectionPostLimit: form.firstCollectionPostLimit,
  }
}

/**
 * The estimate request for the dialog as it stands: the channels, brands,
 * subject kind, cadence, weekly rate and look-back create (or the edit)
 * would send, so the server plans each channel as its run would and the
 * posts, credits and funds state read that one plan.
 */
export function toSocialEstimateRequest(
  form: UniverseFormState,
  universeToken: string | null,
): SocialEstimateRequest {
  return {
    ...(universeToken ? { universeToken } : {}),
    keywords: form.keywords,
    channels: channelInputs(form, universeToken !== null),
    brands: namedBrands(form),
    subjectKind: form.subjectKind,
    marketCode: form.marketCode,
    depthDays: form.depthDays,
    cadence: form.cadence,
    runPostLimit: form.runPostLimit,
    firstCollectionPostLimit: form.firstCollectionPostLimit,
  }
}

/** A Topic edit always sends the full channel list: missing platforms are removed. */
export function toUniverseUpdate(form: UniverseFormState): SocialUniverseUpdate {
  return {
    name: form.name.trim(),
    keywords: form.keywords,
    keywordNotes: currentKeywordNotes(form),
    brief: form.brief,
    labels: universeLabels(form),
    topics: namedTopics(form),
    brands: namedBrands(form),
    subjectKind: form.subjectKind,
    channels: channelInputs(form, true),
    languages: form.languages.slice(0, SOCIAL_MAX_LANGUAGES),
    market: form.market.trim(),
    marketCode: form.marketCode,
    depthDays: form.depthDays,
    cadence: form.cadence,
    runPostLimit: form.runPostLimit,
    firstCollectionPostLimit: form.firstCollectionPostLimit,
  }
}

/** Channels of the Topic that the edit unchecks; their posts and images go. */
export function removedPlatforms(
  universe: SocialUniverseItem | null,
  platforms: Array<SocialPlatform>,
): Array<SocialPlatform> {
  if (!universe) {
    return []
  }
  return sortPlatforms(
    universe.channels
      .map((channel) => channel.platform)
      .filter((platform) => !platforms.includes(platform)),
  )
}

function tagShape(
  brief: SocialTrackerBrief,
  topics: Array<SocialTopicInput>,
  brands: Array<SocialBrandInput>,
): string {
  return JSON.stringify([
    brief.include.trim(),
    brief.exclude.trim(),
    topics
      .filter((topic) => topic.name.trim() !== '')
      .map((topic) => [topic.id ?? null, topic.name.trim(), topic.description.trim()]),
    brands
      .filter((brand) => brand.name.trim() !== '')
      .map((brand) => [
        brand.id ?? null,
        brand.name.trim(),
        (brand.description ?? '').trim(),
        (brand.aliases ?? []).map((alias) => alias.trim()).filter(Boolean),
      ]),
  ])
}

/** True when the edit changes what posts are sorted by (brief, themes or brands). */
export function changesTags(
  universe: SocialUniverseItem | null,
  form: UniverseFormState,
): boolean {
  if (!universe) {
    return false
  }
  return (
    tagShape(universe.brief, universe.topics, universe.brands) !==
    tagShape(form.brief, form.topics, form.brands)
  )
}

/**
 * What a suggestion adds, never replacing anything the user has: the
 * subject's suggested keywords after the user's own, up to
 * SOCIAL_MAX_KEYWORDS (D21b); notes for keywords without one; the brief
 * written from the goal, in each brief field still empty; the suggested
 * brands as brand rows after the user's own, up to SOCIAL_MAX_BRANDS (one
 * per case-insensitive name); the suggested themes after the user's own, up
 * to SOCIAL_MAX_TOPICS (one per case-insensitive name); each channel's suggested searches, then its checked hashtags,
 * after its own, none already there (`socialInputKey`, exact after folding)
 * and searches and hashtags together up to SOCIAL_MAX_KEYWORDS;
 * its checked communities after its own (one per `socialInputKey`, the
 * user's spelling kept). What did not fit is offered as chips.
 */
export function applyUniverseSuggestion(
  form: UniverseFormState,
  suggestion: SocialUniverseSuggestion,
): UniverseFormState {
  const keywords = [
    ...form.keywords,
    ...newSuggestedNames(form.keywords, suggestion.keywords ?? []),
  ].slice(0, Math.max(form.keywords.length, SOCIAL_MAX_KEYWORDS))
  const brands = [
    ...form.brands,
    ...newSuggestedNames(
      form.brands.map((brand) => brand.name),
      suggestion.brands.map((brand) => brand.name),
    ).flatMap((name) => {
      const brand = suggestion.brands.find((entry) => entry.name.trim() === name)
      return brand ? [{ ...brand, name, description: brand.description ?? '' }] : []
    }),
  ].slice(0, Math.max(form.brands.length, SOCIAL_MAX_BRANDS))
  const topics = [
    ...form.topics,
    ...newSuggestedNames(
      form.topics.map((topic) => topic.name),
      suggestion.topics.map((topic) => topic.name),
    ).map((name) => ({
      name,
      description: suggestion.topics.find((topic) => topic.name.trim() === name)?.description ?? '',
    })),
  ].slice(0, Math.max(form.topics.length, SOCIAL_MAX_TOPICS))
  const kept = form.keywordNotes.filter((note) => note.description.trim())
  const notes = suggestion.keywordNotes.filter(
    (note) =>
      keywords.includes(note.keyword) &&
      note.description.trim() &&
      !kept.some((entry) => entry.keyword === note.keyword),
  )
  const searches = { ...form.searches }
  const hashtags = { ...form.hashtags }
  const communities = { ...form.communities }
  for (const platform of SOCIAL_UNIVERSE_PLATFORM_ORDER) {
    const own = form.searches[platform]
    const heldSearches = new Set(own.map((search) => socialInputKey(search.query)))
    const added = (suggestion.searches?.[platform] ?? []).filter((query) => {
      const key = socialInputKey(query)
      if (heldSearches.has(key)) {
        return false
      }
      heldSearches.add(key)
      return true
    })
    searches[platform] = [...own, ...added.map((query) => ({ query }))].slice(
      0,
      Math.max(own.length, SOCIAL_MAX_KEYWORDS - form.hashtags[platform].length),
    )
    const room = SOCIAL_MAX_KEYWORDS - searches[platform].length - form.hashtags[platform].length
    const heldTags = new Set(form.hashtags[platform].map((entry) => socialInputKey(entry.tag)))
    const tags = (suggestion.hashtags?.[platform] ?? []).filter(
      (hashtag) => !heldTags.has(socialInputKey(hashtag.tag)),
    )
    hashtags[platform] = [...form.hashtags[platform], ...tags.slice(0, Math.max(0, room))]
    const held = new Set(
      form.communities[platform].map((entry) => socialInputKey(entry.name)),
    )
    const places = (suggestion.communities?.[platform] ?? []).filter(
      (community) => !held.has(socialInputKey(community.name)),
    )
    communities[platform] = [...form.communities[platform], ...places]
  }
  const brief = {
    include: form.brief.include.trim() ? form.brief.include : (suggestion.brief?.include ?? ''),
    exclude: form.brief.exclude.trim() ? form.brief.exclude : (suggestion.brief?.exclude ?? ''),
  }
  return {
    ...form,
    keywords,
    brands,
    topics,
    keywordNotes: [...kept, ...notes],
    brief,
    searches,
    hashtags,
    communities,
  }
}

/** What one Suggest put on the form: subject keywords, brands and themes by name, the rest per channel. */
export interface UniverseSuggested {
  keywords: Array<string>
  brands: Array<string>
  topics: Array<string>
  searches: Record<SocialPlatform, Array<string>>
  hashtags: Record<SocialPlatform, Array<string>>
  communities: Record<SocialPlatform, Array<string>>
}

/** What a Suggest added (`after` against `before`). */
export function suggestedOf(
  before: Pick<UniverseFormState, 'keywords' | 'brands' | 'topics' | 'searches' | 'hashtags' | 'communities'>,
  after: Pick<UniverseFormState, 'keywords' | 'brands' | 'topics' | 'searches' | 'hashtags' | 'communities'>,
): UniverseSuggested {
  const beforeBrands = new Set(before.brands.map((brand) => brand.name))
  const beforeTopics = new Set(before.topics.map((topic) => topic.name))
  const added = <T>(of: (form: typeof before) => Record<SocialPlatform, Array<T>>, value: (entry: T) => string) => {
    const result = {} as Record<SocialPlatform, Array<string>>
    for (const platform of SOCIAL_UNIVERSE_PLATFORM_ORDER) {
      const had = new Set(of(before)[platform].map(value))
      result[platform] = of(after)[platform].map(value).filter((entry) => !had.has(entry))
    }
    return result
  }
  return {
    keywords: after.keywords.filter((keyword) => !before.keywords.includes(keyword)),
    brands: after.brands.map((brand) => brand.name).filter((name) => !beforeBrands.has(name)),
    topics: after.topics.map((topic) => topic.name).filter((name) => !beforeTopics.has(name)),
    searches: added((form) => form.searches, (search) => search.query),
    hashtags: added((form) => form.hashtags, (hashtag) => hashtag.tag),
    communities: added((form) => form.communities, (community) => community.name),
  }
}

/**
 * Before every Suggest after the first: the last Suggest's subject keywords,
 * brands and themes (unsaved: no id), searches, hashtags
 * and `add` communities still on the form go, so each Suggest replaces its
 * own earlier proposals instead of piling near-duplicates beside them (a
 * channel holds at most one Suggest's worth, SOCIAL_SUGGESTED_KEYWORDS_MAX
 * searches). What the user typed, and a search saved before (it has an id),
 * is kept; the new Suggest adds none of those again (`applyUniverseSuggestion`).
 */
export function withoutStaleSuggestions(
  form: UniverseFormState,
  last: UniverseSuggested | null,
): UniverseFormState {
  if (last === null) {
    return form
  }
  const keywords = form.keywords.filter((keyword) => !last.keywords.includes(keyword))
  const brands = form.brands.filter(
    (brand) => brand.id !== undefined || !last.brands.includes(brand.name),
  )
  const topics = form.topics.filter(
    (topic) => topic.id !== undefined || !last.topics.includes(topic.name),
  )
  const searches = { ...form.searches }
  const hashtags = { ...form.hashtags }
  const communities = { ...form.communities }
  for (const platform of SOCIAL_UNIVERSE_PLATFORM_ORDER) {
    const staleSearches = new Set(last.searches[platform])
    const staleTags = new Set(last.hashtags[platform])
    const staleCommunities = new Set(last.communities[platform])
    searches[platform] = form.searches[platform].filter(
      (search) => search.id !== undefined || !staleSearches.has(search.query),
    )
    hashtags[platform] = form.hashtags[platform].filter((hashtag) => !staleTags.has(hashtag.tag))
    communities[platform] = form.communities[platform].filter(
      (community) => community.mode !== 'add' || !staleCommunities.has(community.name),
    )
  }
  return { ...form, keywords, brands, topics, searches, hashtags, communities }
}

/** Suggested names not on the form yet (case-insensitive), first spelling kept. */
export function newSuggestedNames(
  existing: Array<string>,
  suggested: Array<string>,
): Array<string> {
  const seen = new Set(existing.map((name) => name.trim().toLowerCase()))
  const result: Array<string> = []
  for (const name of suggested) {
    const key = name.trim().toLowerCase()
    if (key && !seen.has(key)) {
      seen.add(key)
      result.push(name.trim())
    }
  }
  return result
}

/**
 * The relevant posts an on-demand Topic's Update may bring: the sum of its
 * channels' next run targets (`nextPlan`). Null while a channel has no plan.
 */
export function universeNextRunTarget(universe: SocialUniverseItem): number | null {
  let total = 0
  for (const channel of universe.channels) {
    if (!channel.nextPlan) {
      return null
    }
    total += channel.nextPlan.target
  }
  return total
}

